import { useState, useCallback } from "react";
import { useAtom } from "jotai";
import { toast } from "react-toastify";
import { DateTime } from "luxon";
import { questionsConfigAtom, stepsConfig } from "../formConfigAuto";
import { buildFormStateFromLeadData } from "../leadAutoDataFormatter";
import { evaluateCondition } from "../leadAutoCompletion";
import { updateLeadAuto } from "../../../../../services/graal-api/leadAutoService";
import useLeadAutoItem from "../useLeadAutoItem";
import {
  isBonusMalusInvalid,
  BONUS_MALUS_ERROR_MESSAGE,
  erreursBonusMalus,
  normaliserBonusMalus,
} from "./bonusMalusValidation";

/**
 * Mapping des types de question (formConfigAuto) vers les types de prop du Control backoffice.
 * Identique au INPUT_TYPES_TO_PROP côté web.
 */
const INPUT_TYPES_TO_PROP = {
  BTN_LIST: "btnList",
  TEXT: "text",
  CHECK_BTN_LIST: "checkBtnList",
  NUMBER_INT: "number",
  DATE: "date",
  SELECT: "select",
  CHECKBOX: "checkbox",
  AUTOCOMPLETE: "autocomplete",
};

// Champs texte véhicule (marque / modèle / version) et regex des caractères autorisés :
// lettres (accents FR : aigu, grave, circonflexe, tréma), chiffres, point, espace et tiret.
const VEHICULE_TEXT_FIELDS = [
  "marque_vehicule",
  "modele_vehicule",
  "version_vehicule",
];
const VEHICULE_ALLOWED_REGEX =
  /^[A-Za-z0-9. éÉàèùÀÈÙâêîôûÂÊÎÔÛëïüÿËÏÜŸ-]*$/;
const VEHICULE_CHARS_ERROR_MESSAGE =
  "Les caractères spéciaux ne sont pas autorisés.";

/**
 * Hook pour gérer l'édition des réponses d'un lead auto dans le backoffice.
 * - Construit un formState plat depuis les données du lead
 * - Fournit onChange, evaluateCondition, commonInputProps (comme côté web)
 * - Envoie le même payload que le formulaire web vers POST /new-leads
 */
const useLeadAutoEdit = (leadData) => {
  const [questionsConfig] = useAtom(questionsConfigAtom);
  const { actions: leadActions } = useLeadAutoItem();

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formState, setFormState] = useState({});
  const [errors, setErrors] = useState({});

  /**
   * Passe en mode édition en initialisant le formState depuis les données du lead
   */
  const startEditing = useCallback(() => {
    const initialFormState = buildFormStateFromLeadData(
      leadData,
      questionsConfig,
    );
    setFormState(initialFormState);
    // Un coefficient bonus/malus enregistré hors plage (ex. 0, PER-2490) est
    // signalé en erreur dès l'ouverture, jusqu'à correction.
    setErrors(erreursBonusMalus(initialFormState));
    setIsEditing(true);
  }, [leadData, questionsConfig]);

  /**
   * Annule l'édition
   */
  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setFormState({});
    setErrors({});
  }, []);

  /**
   * Handler onChange pour les inputs (même signature que côté web)
   */
  const onChangeInput = useCallback((e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Validation des caractères pour les 3 champs texte véhicule (marque / modèle / version)
    if (VEHICULE_TEXT_FIELDS.includes(name)) {
      const isInvalid = !!value && !VEHICULE_ALLOWED_REGEX.test(value);
      setErrors((prev) => ({
        ...prev,
        [name]: isInvalid ? VEHICULE_CHARS_ERROR_MESSAGE : undefined,
      }));
    }

    // Validation des bornes du bonus/malus (0.50 à 3.50)
    if (name.endsWith("bonus_malus")) {
      setErrors((prev) => ({
        ...prev,
        [name]: isBonusMalusInvalid(value)
          ? BONUS_MALUS_ERROR_MESSAGE
          : undefined,
      }));
    }
  }, []);

  /**
   * Génère les props communes pour un Control, similaire à commonInputProps côté web
   */
  const commonInputProps = useCallback(
    (questionConfig) => {
      const isReadOnly = questionConfig.name === "email_utilisateur";

      return {
        error: errors,
        change: onChangeInput,
        labelClassname: "w-100",
        fullWidth: true,
        required: questionConfig.isBooleanBtnList ? false : true,
        btnInline: true,
        name: questionConfig.name,
        label: questionConfig.label,
        value: formState[questionConfig.name] ?? "",
        placeholder: questionConfig.placeholder || "",
        datas: questionConfig.datas,
        dataIndex: "id",
        dataLabel: "name",
        type: INPUT_TYPES_TO_PROP[questionConfig.type],
        isBooleanBtnList: questionConfig.isBooleanBtnList,
        info: questionConfig.info,
        disabled: isReadOnly,
        noBg: true,
        noPaddingOnFormGroup: true,
        noMarginOnFormGroup: true,
        noShadow: true,
        noRounded: true,
        containerStyle: {},
      };
    },
    [formState, errors, onChangeInput],
  );

  /**
   * Filtre les questions d'une catégorie en évaluant les conditions
   */
  const getQuestionsForCategory = useCallback(
    (category) => {
      return Object.entries(questionsConfig).filter(
        ([questionKey, questionCfg]) => {
          const isInCategory = questionCfg.category === category;
          const conditionMet = evaluateCondition(
            questionCfg.condition,
            formState,
          );
          return isInCategory && conditionMet;
        },
      );
    },
    [questionsConfig, formState],
  );

  /**
   * Sauvegarde les modifications en envoyant le même payload que le web
   */
  const saveChanges = useCallback(async () => {
    // Bloquer la sauvegarde tant que des champs sont en erreur (graal refuse
    // aussi un bonus/malus hors plage depuis PER-2490 ; le message est posé
    // ici sous le champ, avant tout envoi)
    if (Object.values(errors).some(Boolean)) {
      toast.error("Corrigez les champs en erreur avant d'enregistrer.");
      return;
    }

    setIsSaving(true);

    try {
      // Normaliser les dates au format yyyy-MM-dd (comme le payload web)
      const normalizedFormState = { ...formState };
      Object.entries(questionsConfig).forEach(([key, config]) => {
        if (config.type === "DATE" && normalizedFormState[key]) {
          const dt = DateTime.fromISO(normalizedFormState[key]);
          if (dt.isValid) {
            normalizedFormState[key] = dt.toFormat("yyyy-MM-dd");
          }
        }
      });

      // Bonus/malus : virgule décimale normalisée en point (PER-2490), comme le site
      Object.keys(normalizedFormState).forEach((key) => {
        if (key.endsWith("bonus_malus")) {
          normalizedFormState[key] = normaliserBonusMalus(normalizedFormState[key]);
        }
      });

      const payload = {
        ...normalizedFormState,
        type_produit: "AUTO",
        id: leadData.id,
        id_utilisateur: leadData.user?.id || null,
        source_lead: leadData.productdata?.source_lead ?? null,
      };

      await updateLeadAuto(payload);
      toast.success("Réponses mises à jour avec succès");
      setIsEditing(false);

      // Rafraîchir le lead pour afficher les nouvelles données
      await leadActions.refreshLead();
    } catch (err) {
      console.error("Erreur lors de la mise à jour du lead:", err);
      if (err.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error("Erreur lors de la sauvegarde des modifications.");
      }
    } finally {
      setIsSaving(false);
    }
  }, [formState, errors, leadData, leadActions, questionsConfig]);

  return {
    isEditing,
    isSaving,
    formState,
    errors,
    questionsConfig,
    stepsConfig,
    startEditing,
    cancelEditing,
    saveChanges,
    commonInputProps,
    getQuestionsForCategory,
    evaluateCondition: (condition) => evaluateCondition(condition, formState),
  };
};

export default useLeadAutoEdit;
